import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  Activity,
  ArrowDownAZ,
  ArrowUpAZ,
  Cat,
  Check,
  ChevronDown,
  ChevronRight,
  CircleUserRound,
  FileLock2,
  FileText,
  HeartPulse,
  HelpCircle,
  ImagePlus,
  Link2,
  LockKeyhole,
  LogOut,
  Paperclip,
  Pencil,
  PenLine,
  Pill,
  Plus,
  RefreshCw,
  Scale,
  ShieldCheck,
  Soup,
  Toilet,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";
import { BlockEditor } from "../components/admin/BlockEditor";
import { Brand } from "../components/Brand";
import { DisplayControls } from "../components/DisplayControls";
import { useAuth } from "../core/auth";
import { createClientId } from "../core/id";
import { useI18n } from "../core/i18n";
import {
  changePassword,
  createGuardianQuestion,
  createMemberRecord,
  createPet,
  createShareLink,
  deleteGuardianQuestion,
  deleteMemberFile,
  deleteMemberRecord,
  loadMemberCase,
  loadMemberDashboard,
  renameMemberFile,
  requestEmailChange,
  revokeShareLink,
  updateCaseDetails,
  updateGuardianQuestion,
  updateMemberProfile,
  updateMemberRecord,
  updatePetProfile,
  uploadMemberFile,
} from "../data/memberRepository";
import {
  buildPreviewCase,
  buildPreviewMemberDashboard,
} from "../data/previewMemberData";
import {
  editableCaseTimeline,
  legacySymptomPrefix,
} from "../data/casePresentation";
import { loadPublicConfig } from "../data/platformRepository";
import { useScrollDirection } from "../hooks/useScrollDirection";
import { defaultManagedPageCopy } from "../content/managedPageCopy";
import type {
  CareQuestion,
  CaseData,
  EditorBlock,
  MemberDashboardData,
  MemberFileSummary,
  PublicPlatformConfig,
  Severity,
  TimelineItem,
} from "../types";

type Modal =
  | "pet"
  | "pet-edit"
  | "profile"
  | "share"
  | "profile-photo"
  | "record-edit"
  | "question"
  | "file"
  | null;
type RecordCategory =
  | "symptom"
  | "measurement"
  | "medication"
  | "observation"
  | "visit";
type QuickKind =
  | "photo"
  | "stool"
  | "meal"
  | "vomit"
  | "measure"
  | "medication"
  | "visit"
  | "other";

const quickKinds: Array<{
  key: QuickKind;
  label: string;
  detail: string;
  icon: typeof Activity;
  category: RecordCategory;
  severity: Severity;
  title: string;
  block: EditorBlock["type"];
}> = [
  {
    key: "photo",
    label: "사진부터",
    detail: "장면 + 한 줄",
    icon: ImagePlus,
    category: "observation",
    severity: "info",
    title: "오늘 관찰한 변화",
    block: "paragraph",
  },
  {
    key: "stool",
    label: "배변과 소변",
    detail: "설사, 소변 변화",
    icon: Toilet,
    category: "symptom",
    severity: "attention",
    title: "배변과 소변 변화",
    block: "stool",
  },
  {
    key: "meal",
    label: "식사와 물",
    detail: "먹고 마신 양",
    icon: Soup,
    category: "observation",
    severity: "attention",
    title: "식사와 물 섭취 변화",
    block: "meal",
  },
  {
    key: "vomit",
    label: "구토",
    detail: "횟수와 내용물",
    icon: Activity,
    category: "symptom",
    severity: "attention",
    title: "구토 증상",
    block: "symptom",
  },
  {
    key: "measure",
    label: "체중과 체온",
    detail: "측정값",
    icon: Scale,
    category: "measurement",
    severity: "info",
    title: "측정값 기록",
    block: "measurement",
  },
  {
    key: "medication",
    label: "투약과 처치",
    detail: "복용과 반응",
    icon: Pill,
    category: "medication",
    severity: "info",
    title: "투약과 처치 기록",
    block: "medication",
  },
  {
    key: "visit",
    label: "병원 다녀옴",
    detail: "검사와 설명",
    icon: HeartPulse,
    category: "visit",
    severity: "info",
    title: "병원 방문 기록",
    block: "quote",
  },
  {
    key: "other",
    label: "다른 변화",
    detail: "행동, 피부 등",
    icon: Plus,
    category: "observation",
    severity: "info",
    title: "새로운 상태 변화",
    block: "paragraph",
  },
];

function localNow() {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 16);
}

function newDetailBlock(
  type: EditorBlock["type"] = "paragraph",
): EditorBlock[] {
  return [
    {
      id: createClientId("record-block-"),
      type,
      content: "",
      source: "guardian_observation",
    },
  ];
}

function sexLabel(value: string | undefined, ko: boolean) {
  if (value === "male") return ko ? "수컷" : "Male";
  if (value === "female") return ko ? "암컷" : "Female";
  return ko ? "성별 미등록" : "Sex not set";
}

export function MemberPage() {
  const { locale } = useI18n();
  const { user, csrf, logout, refresh } = useAuth();
  const ko = locale === "ko";
  const [dashboard, setDashboard] = useState<MemberDashboardData | null>(null);
  const [caseData, setCaseData] = useState<CaseData | null>(null);
  const [selectedPetId, setSelectedPetId] = useState("");
  const [loading, setLoading] = useState(true);
  const [caseLoading, setCaseLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [modal, setModal] = useState<Modal>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [quickKind, setQuickKind] = useState<QuickKind>("photo");
  const [occurredAt, setOccurredAt] = useState(localNow);
  const [recordTitle, setRecordTitle] = useState("오늘 관찰한 변화");
  const [recordNote, setRecordNote] = useState("");
  const [severity, setSeverity] = useState<Severity>("info");
  const [category, setCategory] = useState<RecordCategory>("observation");
  const [detailBlocks, setDetailBlocks] =
    useState<EditorBlock[]>(newDetailBlock());
  const [showDetailBlocks, setShowDetailBlocks] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [sort, setSort] = useState<"asc" | "desc">("asc");
  const [lastShareUrl, setLastShareUrl] = useState("");
  const [editingRecord, setEditingRecord] = useState<TimelineItem | null>(null);
  const [editingBlocks, setEditingBlocks] = useState<EditorBlock[]>([]);
  const [editingQuestion, setEditingQuestion] = useState<CareQuestion | null>(
    null,
  );
  const [editingFile, setEditingFile] = useState<MemberFileSummary | null>(
    null,
  );
  const [platformConfig, setPlatformConfig] =
    useState<PublicPlatformConfig | null>(null);
  const preview = Boolean(user?.id.startsWith("preview-"));
  const managedCopy = (platformConfig?.pageCopy || defaultManagedPageCopy)
    .member[locale];

  useEffect(() => {
    const controller = new AbortController();
    loadPublicConfig(controller.signal)
      .then(setPlatformConfig)
      .catch(() => undefined);
    return () => controller.abort();
  }, []);

  const showNotice = (message: string) => {
    setNotice(message);
    window.setTimeout(() => setNotice(""), 3200);
  };
  const refreshDashboard = async () => {
    if (!user) return;
    setLoading(true);
    setError("");
    if (preview) {
      const next = buildPreviewMemberDashboard(user);
      setDashboard(next);
      setSelectedPetId((current) => current || next.pets[0]?.id || "");
      setCaseData((current) => current || buildPreviewCase());
      setLoading(false);
      return;
    }
    try {
      const next = await loadMemberDashboard();
      setDashboard(next);
      setSelectedPetId((current) => current || next.pets[0]?.id || "");
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "기록을 불러오지 못했습니다.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    const loadInitial = async () => {
      if (!user) return;
      if (user.id.startsWith("preview-")) {
        await Promise.resolve();
        if (!active) return;
        const next = buildPreviewMemberDashboard(user);
        setDashboard(next);
        setSelectedPetId((current) => current || next.pets[0]?.id || "");
        setCaseData(buildPreviewCase());
        setLoading(false);
        return;
      }
      try {
        const next = await loadMemberDashboard();
        if (!active) return;
        setDashboard(next);
        setSelectedPetId((current) => current || next.pets[0]?.id || "");
      } catch (cause) {
        if (active)
          setError(
            cause instanceof Error
              ? cause.message
              : "기록을 불러오지 못했습니다.",
          );
      } finally {
        if (active) setLoading(false);
      }
    };
    void loadInitial();
    return () => {
      active = false;
    };
  }, [user]);

  const selectedPet =
    dashboard?.pets.find((pet) => pet.id === selectedPetId) ||
    dashboard?.pets[0];
  const selectedCase =
    dashboard?.cases.find(
      (item) => selectedPet && item.petIds.includes(selectedPet.id),
    ) || dashboard?.cases[0];
  const selectedCaseId = selectedCase?.id || "";
  const selectedPetRecord = caseData?.pets.find(
    (pet) => pet.id === selectedPet?.id,
  );

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const loadCase = async () => {
      await Promise.resolve();
      if (!active) return;
      if (user?.id.startsWith("preview-")) {
        setCaseData((current) => current || buildPreviewCase());
        setCaseLoading(false);
        return;
      }
      if (!selectedCaseId) {
        setCaseData(null);
        setCaseLoading(false);
        return;
      }
      setCaseLoading(true);
      try {
        const next = await loadMemberCase(selectedCaseId, controller.signal);
        if (active) setCaseData(next);
      } catch (cause) {
        if (active && cause instanceof Error && cause.name !== "AbortError")
          setError(cause.message);
      } finally {
        if (active) setCaseLoading(false);
      }
    };
    void loadCase();
    return () => {
      active = false;
      controller.abort();
    };
  }, [selectedCaseId, user?.id]);

  const chooseKind = (key: QuickKind) => {
    const option = quickKinds.find((item) => item.key === key)!;
    setQuickKind(key);
    setCategory(option.category);
    setSeverity(option.severity);
    setRecordTitle(option.title);
    setDetailBlocks(newDetailBlock(option.block));
    setComposerOpen(true);
  };

  const resetComposer = () => {
    setComposerOpen(false);
    setQuickKind("photo");
    setOccurredAt(localNow());
    setRecordTitle("오늘 관찰한 변화");
    setRecordNote("");
    setSeverity("info");
    setCategory("observation");
    setDetailBlocks(newDetailBlock());
    setShowDetailBlocks(false);
    setFiles([]);
    setUploadProgress(0);
  };

  const submitRecord = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedPet || !selectedCase)
      return setError(
        ko ? "먼저 반려동물을 등록해 주세요." : "Add a pet first.",
      );
    if (
      !recordNote.trim() &&
      !files.length &&
      !detailBlocks.some((block) => block.content.trim())
    )
      return setError(
        ko
          ? "사진을 넣거나 달라진 점을 한 줄로 적어 주세요."
          : "Add a photo or a short note.",
      );
    setSaving(true);
    setError("");
    try {
      const assetBlocks: EditorBlock[] = [];
      const previewFiles: MemberFileSummary[] = [];
      for (const [index, file] of files.entries()) {
        if (preview) {
          const assetId = createClientId("preview-asset-");
          const assetKind = file.type.startsWith("image/")
            ? "image"
            : file.type.startsWith("video/")
              ? "video"
              : "document";
          assetBlocks.push({
            id: createClientId("asset-block-"),
            type: assetKind === "document" ? "paragraph" : "media",
            content: file.name,
            source: "document",
            assetId,
            assetName: file.name,
            assetKind,
          });
          previewFiles.push({
            id: assetId,
            caseId: selectedCase.id,
            petId: selectedPet.id,
            kind: assetKind === "document" ? "document" : "media",
            title: file.name,
            createdAt: occurredAt,
            folderPath: "로컬 미리보기 전용",
            href: URL.createObjectURL(file),
          });
          setUploadProgress(Math.round(((index + 1) * 100) / files.length));
          continue;
        }
        const form = new FormData();
        form.set("caseId", selectedCase.id);
        form.set("petId", selectedPet.id);
        form.set(
          "kind",
          file.type.startsWith("image/") || file.type.startsWith("video/")
            ? "media"
            : "document",
        );
        form.set("title", recordTitle);
        form.set("file", file);
        const result = await uploadMemberFile(form, csrf, (value) =>
          setUploadProgress(Math.round((index * 100 + value) / files.length)),
        );
        assetBlocks.push({
          id: createClientId("asset-block-"),
          type:
            file.type.startsWith("image/") || file.type.startsWith("video/")
              ? "media"
              : "paragraph",
          content: file.name,
          source: "document",
          assetId: result.assetId,
          assetName: file.name,
          assetKind: file.type.startsWith("image/")
            ? "image"
            : file.type.startsWith("video/")
              ? "video"
              : "document",
        });
      }
      const titleBlock: EditorBlock = {
        id: createClientId("title-block-"),
        type: "heading2",
        content: recordTitle,
        source: "guardian_observation",
      };
      const summaryBlock: EditorBlock = {
        id: createClientId("summary-block-"),
        type:
          quickKinds.find((item) => item.key === quickKind)?.block ||
          "paragraph",
        content: recordNote.trim(),
        source: "guardian_observation",
      };
      const blocks: EditorBlock[] = [
        titleBlock,
        summaryBlock,
        ...assetBlocks,
        ...(showDetailBlocks ? detailBlocks : []),
      ].filter((block) => block.content.trim());
      const detail = [
        recordNote.trim(),
        ...detailBlocks.map((block) => block.content.trim()).filter(Boolean),
        files.length ? `${files.length}개 파일 첨부` : "",
      ]
        .filter(Boolean)
        .join("\n");
      if (preview) {
        const eventId = createClientId("preview-event-");
        const item: TimelineItem = {
          id: eventId,
          date: occurredAt.slice(0, 10),
          time: occurredAt.slice(11, 16),
          petIds: [selectedPet.id],
          category: category === "measurement" ? "observation" : category,
          title: { ko: recordTitle.trim(), en: recordTitle.trim() },
          description: { ko: detail, en: detail },
          source: {
            label: { ko: "보호자 기록", en: "Guardian record" },
            kind: "guardian_observation",
          },
          severity,
          blocks,
        };
        setCaseData((current) =>
          current
            ? {
                ...current,
                timeline: [...current.timeline, item],
                lastUpdatedAt: new Date().toISOString(),
              }
            : current,
        );
        setDashboard((current) =>
          current
            ? {
                ...current,
                files: [...previewFiles, ...current.files],
                cases: current.cases.map((entry) =>
                  entry.id === selectedCase.id
                    ? { ...entry, updatedAt: new Date().toISOString() }
                    : entry,
                ),
              }
            : current,
        );
        resetComposer();
        showNotice(
          ko ? "미리보기 기록을 남겼습니다." : "Preview record saved.",
        );
        return;
      }
      await createMemberRecord(
        {
          caseId: selectedCase.id,
          petIds: [selectedPet.id],
          category,
          occurredAt,
          title: recordTitle.trim(),
          detail,
          severity,
          blocks,
        },
        csrf,
      );
      resetComposer();
      showNotice(ko ? "기록을 남겼습니다." : "Record saved.");
      await refreshDashboard();
      setCaseData(await loadMemberCase(selectedCase.id));
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "기록을 저장하지 못했습니다.",
      );
    } finally {
      setSaving(false);
    }
  };

  const submitPet = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    if (preview) {
      const petId = createClientId("preview-pet-");
      const name = String(data.get("name"));
      setDashboard((current) =>
        current
          ? {
              ...current,
              pets: [
                ...current.pets,
                {
                  id: petId,
                  name,
                  species: String(data.get("species")),
                  sex: String(data.get("sex")),
                  updatedAt: new Date().toISOString(),
                },
              ],
              cases: current.cases.map((entry, index) =>
                index === 0
                  ? { ...entry, petIds: [...entry.petIds, petId] }
                  : entry,
              ),
            }
          : current,
      );
      setSelectedPetId(petId);
      setModal(null);
      showNotice(
        ko ? "미리보기에 반려동물을 등록했습니다." : "Preview pet added.",
      );
      return;
    }
    try {
      const result = await createPet(
        {
          name: String(data.get("name")),
          species: String(data.get("species")),
          sex: String(data.get("sex")),
          estimatedBirth: String(data.get("estimatedBirth") || ""),
        },
        csrf,
      );
      setSelectedPetId(result.petId);
      setModal(null);
      showNotice(ko ? "반려동물을 등록했습니다." : "Pet added.");
      await refreshDashboard();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "등록하지 못했습니다.");
    }
  };

  const submitProfilePhoto = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedPet || !selectedCase) return;
    const form = new FormData(event.currentTarget);
    form.set("caseId", selectedCase.id);
    form.set("petId", selectedPet.id);
    form.set("kind", "profile");
    form.set("title", `${selectedPet.name} 대표 사진`);
    if (preview) {
      const file = form.get("file");
      if (file instanceof File)
        setDashboard((current) =>
          current
            ? {
                ...current,
                pets: current.pets.map((pet) =>
                  pet.id === selectedPet.id
                    ? { ...pet, profileImageUrl: URL.createObjectURL(file) }
                    : pet,
                ),
              }
            : current,
        );
      setModal(null);
      showNotice(
        ko
          ? "미리보기 대표 사진을 바꿘습니다."
          : "Preview profile photo updated.",
      );
      return;
    }
    try {
      await uploadMemberFile(form, csrf, setUploadProgress);
      setModal(null);
      showNotice(ko ? "대표 사진을 바꿨습니다." : "Profile photo updated.");
      await refreshDashboard();
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "사진을 올리지 못했습니다.",
      );
    }
  };

  const submitShare = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedCase) return;
    const form = new FormData(event.currentTarget);
    if (preview) {
      const expiresInDays = Number(form.get("expiresInDays"));
      const url = `${window.location.origin}/share/preview`;
      const share = {
        id: createClientId("preview-share-"),
        caseId: selectedCase.id,
        expiresAt: new Date(
          Date.now() + expiresInDays * 86_400_000,
        ).toISOString(),
        viewCount: 0,
        active: true,
      };
      setDashboard((current) =>
        current
          ? {
              ...current,
              shares: [
                share,
                ...current.shares.map((item) => ({ ...item, active: false })),
              ],
            }
          : current,
      );
      setLastShareUrl(url);
      await navigator.clipboard.writeText(url).catch(() => undefined);
      showNotice(
        ko
          ? "미리보기 보안 링크를 만들고 복사했습니다."
          : "Preview link created and copied.",
      );
      return;
    }
    try {
      const result = await createShareLink(
        selectedCase.id,
        Number(form.get("expiresInDays")),
        csrf,
      );
      setLastShareUrl(result.url);
      await navigator.clipboard.writeText(result.url).catch(() => undefined);
      showNotice(
        ko
          ? "보안 링크를 만들고 복사했습니다."
          : "Secure link created and copied.",
      );
      await refreshDashboard();
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "공유 링크를 만들지 못했습니다.",
      );
    }
  };

  const timeline = useMemo(
    () =>
      (caseData ? editableCaseTimeline(caseData) : [])
        .filter((item) => !selectedPet || item.petIds.includes(selectedPet.id))
        .sort((a, b) => {
          const result = `${a.date} ${a.time || ""}`.localeCompare(
            `${b.date} ${b.time || ""}`,
          );
          return sort === "asc" ? result : -result;
        }),
    [caseData, selectedPet, sort],
  );
  const petFiles = (dashboard?.files || []).filter(
    (file) => !selectedPet || file.petId === selectedPet.id,
  );
  const activeShare = dashboard?.shares.find(
    (share) => share.caseId === selectedCase?.id && share.active,
  );
  const guardianQuestions = (caseData?.questions || []).filter(
    (question) =>
      !selectedPet ||
      !question.petIds?.length ||
      question.petIds.includes(selectedPet.id),
  );
  const removeActiveShare = async () => {
    if (!activeShare) return;
    if (preview)
      setDashboard((current) =>
        current
          ? {
              ...current,
              shares: current.shares.map((item) =>
                item.id === activeShare.id
                  ? {
                      ...item,
                      active: false,
                      revokedAt: new Date().toISOString(),
                    }
                  : item,
              ),
            }
          : current,
      );
    else {
      await revokeShareLink(activeShare.id, csrf);
      await refreshDashboard();
    }
    showNotice(ko ? "공유 링크를 철회했습니다." : "Link revoked.");
    setModal(null);
  };

  const openRecordEdit = (item: TimelineItem) => {
    setEditingRecord(item);
    setEditingBlocks(
      item.blocks?.length
        ? item.blocks.map((block) => ({ ...block }))
        : [
            {
              id: createClientId("edit-block-"),
              type: "paragraph",
              content: item.description.ko,
              source: "guardian_observation",
            },
          ],
    );
    setModal("record-edit");
  };

  const submitPetEdit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedPet || !selectedCase) return;
    const data = new FormData(event.currentTarget);
    const input = {
      petId: selectedPet.id,
      caseId: selectedCase.id,
      name: String(data.get("name")),
      species: String(data.get("species")),
      sex: String(data.get("sex")),
      estimatedBirth: String(data.get("estimatedBirth") || ""),
      adoptionDate: String(data.get("adoptionDate") || ""),
      adoptionSource: String(data.get("adoptionSource") || ""),
      clinicVisitDate: String(data.get("clinicVisitDate") || ""),
      registration: String(data.get("registration") || ""),
      insurance: String(data.get("insurance") || ""),
      firstCondition: String(data.get("firstCondition") || ""),
      intakeStatement: String(data.get("intakeStatement") || ""),
      appetite: String(data.get("appetite") || ""),
      feeding: String(data.get("feeding") || ""),
      stool: String(data.get("stool") || ""),
      personality: String(data.get("personality") || ""),
    };
    setSaving(true);
    setError("");
    try {
      if (preview) {
        const localized = (value: string) => ({ ko: value, en: value });
        setDashboard((current) =>
          current
            ? {
                ...current,
                pets: current.pets.map((pet) =>
                  pet.id === selectedPet.id
                    ? {
                        ...pet,
                        name: input.name,
                        species: input.species,
                        sex: input.sex,
                      }
                    : pet,
                ),
              }
            : current,
        );
        setCaseData((current) =>
          current
            ? {
                ...current,
                clinicVisitDate: input.clinicVisitDate,
                pets: current.pets.map((pet) =>
                  pet.id === selectedPet.id
                    ? {
                        ...pet,
                        name: localized(input.name),
                        species: input.species as "cat" | "dog",
                        sex: localized(
                          input.sex === "male"
                            ? "수컷"
                            : input.sex === "female"
                              ? "암컷"
                              : "확인 전",
                        ),
                        estimatedBirth: input.estimatedBirth,
                        adoptionDate: input.adoptionDate,
                        adoptionSource: localized(input.adoptionSource),
                        registration: input.registration,
                        insurance: localized(input.insurance),
                        firstCondition: localized(input.firstCondition),
                        intakeStatement: localized(input.intakeStatement),
                        appetite: localized(input.appetite),
                        feeding: localized(input.feeding),
                        stool: localized(input.stool),
                        personality: localized(input.personality),
                      }
                    : pet,
                ),
                lastUpdatedAt: new Date().toISOString(),
              }
            : current,
        );
      } else {
        await updatePetProfile(input, csrf);
        await updateCaseDetails(
          { caseId: selectedCase.id, clinicVisitDate: input.clinicVisitDate },
          csrf,
        );
        await refreshDashboard();
        setCaseData(await loadMemberCase(selectedCase.id));
      }
      setModal(null);
      showNotice(ko ? "반려동물 정보를 수정했습니다." : "Pet details updated.");
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "반려동물 정보를 수정하지 못했습니다.",
      );
    } finally {
      setSaving(false);
    }
  };

  const submitRecordEdit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editingRecord || !selectedCase) return;
    const data = new FormData(event.currentTarget);
    const occurredAt = String(data.get("occurredAt"));
    const title = String(data.get("title")).trim();
    const detail = String(data.get("detail")).trim();
    const nextCategory = editingRecord.id.startsWith(legacySymptomPrefix)
      ? "symptom"
      : (String(data.get("category")) as RecordCategory);
    const nextSeverity = String(data.get("severity")) as Severity;
    setSaving(true);
    setError("");
    try {
      if (preview) {
        setCaseData((current) =>
          current
            ? editingRecord.id.startsWith(legacySymptomPrefix)
              ? {
                  ...current,
                  symptoms: current.symptoms.map((item) =>
                    `${legacySymptomPrefix}${item.id}` === editingRecord.id
                      ? {
                          ...item,
                          startedAt: occurredAt,
                          title: { ko: title, en: title },
                          detail: { ko: detail, en: detail },
                          severity: nextSeverity,
                        }
                      : item,
                  ),
                  lastUpdatedAt: new Date().toISOString(),
                }
              : {
                  ...current,
                  timeline: current.timeline.map((item) =>
                    item.id === editingRecord.id
                      ? {
                          ...item,
                          date: occurredAt.slice(0, 10),
                          time: occurredAt.slice(11, 16),
                          title: { ko: title, en: title },
                          description: { ko: detail, en: detail },
                          category:
                            nextCategory === "measurement"
                              ? "observation"
                              : nextCategory,
                          severity: nextSeverity,
                          blocks: editingBlocks,
                        }
                      : item,
                  ),
                  lastUpdatedAt: new Date().toISOString(),
                }
            : current,
        );
      } else {
        await updateMemberRecord(
          {
            eventId: editingRecord.id,
            caseId: selectedCase.id,
            petIds: editingRecord.petIds,
            category: nextCategory,
            occurredAt,
            title,
            detail,
            severity: nextSeverity,
            blocks: editingBlocks,
          },
          csrf,
        );
        setCaseData(await loadMemberCase(selectedCase.id));
      }
      setEditingRecord(null);
      setEditingBlocks([]);
      setModal(null);
      showNotice(ko ? "기록을 수정했습니다." : "Record updated.");
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "기록을 수정하지 못했습니다.",
      );
    } finally {
      setSaving(false);
    }
  };

  const removeRecord = async (item: TimelineItem) => {
    if (
      !selectedCase ||
      !window.confirm(
        ko
          ? "이 기록을 삭제할까요? 첨부한 원본 파일은 별도로 남습니다."
          : "Delete this record? Attached source files remain.",
      )
    )
      return;
    setSaving(true);
    setError("");
    try {
      if (preview)
        setCaseData((current) =>
          current
            ? item.id.startsWith(legacySymptomPrefix)
              ? {
                  ...current,
                  symptoms: current.symptoms.filter(
                    (symptom) =>
                      `${legacySymptomPrefix}${symptom.id}` !== item.id,
                  ),
                  lastUpdatedAt: new Date().toISOString(),
                }
              : {
                  ...current,
                  timeline: current.timeline.filter(
                    (record) => record.id !== item.id,
                  ),
                  symptoms: current.symptoms.filter(
                    (symptom) => symptom.id !== `symptom-${item.id}`,
                  ),
                  lastUpdatedAt: new Date().toISOString(),
                }
            : current,
        );
      else {
        await deleteMemberRecord(selectedCase.id, item.id, csrf);
        setCaseData(await loadMemberCase(selectedCase.id));
      }
      showNotice(ko ? "기록을 삭제했습니다." : "Record deleted.");
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "기록을 삭제하지 못했습니다.",
      );
    } finally {
      setSaving(false);
    }
  };

  const openQuestion = (question: CareQuestion | null = null) => {
    setEditingQuestion(question);
    setModal("question");
  };
  const submitQuestion = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedPet || !selectedCase) return;
    const data = new FormData(event.currentTarget);
    const title = String(data.get("title")).trim();
    const detail = String(data.get("detail")).trim();
    setSaving(true);
    setError("");
    try {
      if (preview) {
        setCaseData((current) => {
          if (!current) return current;
          const next = editingQuestion
            ? current.questions.map((item) =>
                item.id === editingQuestion.id
                  ? {
                      ...item,
                      petIds: [selectedPet.id],
                      title: { ko: title, en: title },
                      detail: { ko: detail, en: detail },
                    }
                  : item,
              )
            : [
                ...current.questions,
                {
                  id: createClientId("preview-question-"),
                  petIds: [selectedPet.id],
                  title: { ko: title, en: title },
                  detail: { ko: detail, en: detail },
                  checked: false,
                },
              ];
          return {
            ...current,
            questions: next,
            lastUpdatedAt: new Date().toISOString(),
          };
        });
      } else if (editingQuestion)
        await updateGuardianQuestion(
          {
            caseId: selectedCase.id,
            id: editingQuestion.id,
            petIds: [selectedPet.id],
            title,
            detail,
          },
          csrf,
        );
      else
        await createGuardianQuestion(
          { caseId: selectedCase.id, petId: selectedPet.id, title, detail },
          csrf,
        );
      if (!preview) setCaseData(await loadMemberCase(selectedCase.id));
      setEditingQuestion(null);
      setModal(null);
      showNotice(ko ? "궁금한 점을 저장했습니다." : "Question saved.");
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "궁금한 점을 저장하지 못했습니다.",
      );
    } finally {
      setSaving(false);
    }
  };

  const removeQuestion = async (question: CareQuestion) => {
    if (
      !selectedCase ||
      !window.confirm(ko ? "이 내용을 삭제할까요?" : "Delete this question?")
    )
      return;
    if (preview)
      setCaseData((current) =>
        current
          ? {
              ...current,
              questions: current.questions.filter(
                (item) => item.id !== question.id,
              ),
              lastUpdatedAt: new Date().toISOString(),
            }
          : current,
      );
    else {
      await deleteGuardianQuestion(selectedCase.id, question.id, csrf);
      setCaseData(await loadMemberCase(selectedCase.id));
    }
    showNotice(ko ? "궁금한 점을 삭제했습니다." : "Question deleted.");
  };

  const openFileEdit = (file: MemberFileSummary) => {
    setEditingFile(file);
    setModal("file");
  };
  const submitFileEdit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editingFile) return;
    const title = String(
      new FormData(event.currentTarget).get("title") || "",
    ).trim();
    setSaving(true);
    setError("");
    try {
      if (preview) {
        setDashboard((current) =>
          current
            ? {
                ...current,
                files: current.files.map((file) =>
                  file.id === editingFile.id ? { ...file, title } : file,
                ),
              }
            : current,
        );
        setCaseData((current) =>
          current
            ? editingFile.kind === "media"
              ? {
                  ...current,
                  media: current.media.map((file) =>
                    file.id === editingFile.id
                      ? { ...file, title: { ko: title, en: title } }
                      : file,
                  ),
                }
              : {
                  ...current,
                  documents: current.documents.map((file) =>
                    file.id === editingFile.id
                      ? { ...file, title: { ko: title, en: title } }
                      : file,
                  ),
                }
            : current,
        );
      } else {
        await renameMemberFile(
          {
            caseId: editingFile.caseId,
            assetId: editingFile.id,
            kind: editingFile.kind,
            title,
          },
          csrf,
        );
        await refreshDashboard();
        setCaseData(await loadMemberCase(editingFile.caseId));
      }
      setEditingFile(null);
      setModal(null);
      showNotice(ko ? "파일 이름을 바꿨습니다." : "File name updated.");
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "파일 이름을 바꾸지 못했습니다.",
      );
    } finally {
      setSaving(false);
    }
  };

  const removeFile = async () => {
    if (
      !editingFile ||
      !window.confirm(
        ko
          ? "이 파일을 삭제할까요? Google Drive에서는 휴지통으로 이동합니다."
          : "Delete this file? It will be moved to the Google Drive trash.",
      )
    )
      return;
    setSaving(true);
    setError("");
    try {
      if (preview) {
        setDashboard((current) =>
          current
            ? {
                ...current,
                files: current.files.filter(
                  (file) => file.id !== editingFile.id,
                ),
              }
            : current,
        );
        setCaseData((current) =>
          current
            ? {
                ...current,
                media: current.media.filter(
                  (file) => file.id !== editingFile.id,
                ),
                documents: current.documents.filter(
                  (file) => file.id !== editingFile.id,
                ),
                timeline: current.timeline.map((item) => ({
                  ...item,
                  blocks: item.blocks?.filter(
                    (block) => block.assetId !== editingFile.id,
                  ),
                })),
              }
            : current,
        );
      } else {
        await deleteMemberFile(
          {
            caseId: editingFile.caseId,
            assetId: editingFile.id,
            kind: editingFile.kind,
          },
          csrf,
        );
        await refreshDashboard();
        setCaseData(await loadMemberCase(editingFile.caseId));
      }
      setEditingFile(null);
      setModal(null);
      showNotice(ko ? "파일을 삭제했습니다." : "File deleted.");
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "파일을 삭제하지 못했습니다.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="member-unified-shell">
        <header className="member-simple-header">
          <Brand />
        </header>
        <main className="member-loading">
          <span className="loading-ring" />
          <p>
            {ko
              ? "내 반려동물 기록을 불러오고 있습니다."
              : "Loading your pet records."}
          </p>
        </main>
      </div>
    );
  if (!dashboard)
    return (
      <div className="member-unified-shell">
        <header className="member-simple-header">
          <Brand />
        </header>
        <main>
          <ConnectionState onRetry={() => void refreshDashboard()} ko={ko} />
        </main>
      </div>
    );

  return (
    <div className="member-unified-shell">
      <header className="member-simple-header">
        <Brand />
        {dashboard.pets.length > 1 && (
          <label className="member-header-pet-select">
            <Cat />
            <span>
              <small>{ko ? "기록 중" : "Recording for"}</small>
              <select
                value={selectedPet?.id || ""}
                onChange={(event) => setSelectedPetId(event.target.value)}
                aria-label={ko ? "현재 반려동물 선택" : "Choose current pet"}
              >
                {dashboard.pets.map((pet) => (
                  <option key={pet.id} value={pet.id}>
                    {pet.name}
                  </option>
                ))}
              </select>
            </span>
            <ChevronDown />
          </label>
        )}
        <div className="member-simple-actions">
          <span className="private-indicator">
            <LockKeyhole />
            {preview
              ? ko
                ? "화면 미리보기, 저장 안 됨"
                : "Preview, not saved"
              : ko
                ? "내 비공개 기록"
                : "Private records"}
          </span>
          <DisplayControls />
          <button
            className="member-account-button"
            onClick={() => setModal("profile")}
            aria-label={ko ? "회원정보 열기" : "Open account"}
          >
            {user?.name?.slice(0, 1) || "U"}
          </button>
        </div>
      </header>
      <main className="member-unified-main">
        {notice && (
          <div className="member-toast">
            <Check />
            {notice}
          </div>
        )}
        {error && (
          <div className="member-error" role="alert">
            <span>{error}</span>
            <button onClick={() => setError("")}>
              <X />
            </button>
          </div>
        )}
        <section className="member-welcome">
          <span>{managedCopy.eyebrow}</span>
          <h1>{managedCopy.title}</h1>
          <p>{managedCopy.body}</p>
        </section>

        {!dashboard.pets.length ? (
          <EmptyPets ko={ko} onAdd={() => setModal("pet")} />
        ) : (
          <>
            <section
              className="pet-switcher"
              aria-label={ko ? "반려동물 선택" : "Choose a pet"}
            >
              {dashboard.pets.map((pet) => (
                <button
                  key={pet.id}
                  className={selectedPet?.id === pet.id ? "active" : ""}
                  onClick={() => setSelectedPetId(pet.id)}
                >
                  {pet.profileImageUrl ? (
                    <img src={pet.profileImageUrl} alt="" />
                  ) : (
                    <span>
                      <Cat />
                    </span>
                  )}
                  <strong>{pet.name}</strong>
                  <small>
                    {pet.species === "cat"
                      ? ko
                        ? "고양이"
                        : "Cat"
                      : ko
                        ? "강아지"
                        : "Dog"}
                  </small>
                </button>
              ))}
              <button className="add-pet" onClick={() => setModal("pet")}>
                <Plus />
                <strong>{ko ? "반려동물 추가" : "Add pet"}</strong>
              </button>
            </section>

            {selectedPet && (
              <section className="selected-pet-bar">
                <div className="selected-pet-photo">
                  {selectedPet.profileImageUrl ? (
                    <img
                      src={selectedPet.profileImageUrl}
                      alt={`${selectedPet.name} ${ko ? "대표 사진" : "profile"}`}
                    />
                  ) : (
                    <Cat />
                  )}
                </div>
                <div>
                  <span>
                    {selectedPet.species === "cat"
                      ? ko
                        ? "고양이"
                        : "Cat"
                      : ko
                        ? "강아지"
                        : "Dog"}
                    , {sexLabel(selectedPet.sex, ko)}
                  </span>
                  <h2>{selectedPet.name}</h2>
                  <p>
                    <ShieldCheck />
                    {activeShare
                      ? ko
                        ? "현재 병원 공유 중"
                        : "Currently shared"
                      : ko
                        ? "공유 전까지 나만 볼 수 있어요"
                        : "Only you can see this until shared"}
                  </p>
                </div>
                <div className="selected-pet-actions">
                  <button
                    className="secondary-button"
                    disabled={!selectedPetRecord}
                    onClick={() => setModal("pet-edit")}
                  >
                    <Pencil />
                    {ko ? "정보 수정" : "Edit details"}
                  </button>
                  <button
                    className="secondary-button"
                    onClick={() => setModal("profile-photo")}
                  >
                    <ImagePlus />
                    {ko ? "사진 바꾸기" : "Change photo"}
                  </button>
                </div>
              </section>
            )}

            <section
              className={`unified-composer ${composerOpen ? "open" : ""}`}
              id="new-record"
            >
              <header>
                <div>
                  <span>{ko ? "새 기록" : "New record"}</span>
                  <h2>{managedCopy.sectionTitle}</h2>
                  <p>{managedCopy.sectionBody}</p>
                </div>
                {composerOpen && (
                  <button
                    className="icon-button"
                    onClick={resetComposer}
                    aria-label={ko ? "작성 닫기" : "Close composer"}
                  >
                    <X />
                  </button>
                )}
              </header>
              <div className="quick-kind-grid">
                {quickKinds.map(({ key, label, detail, icon: Icon }) => (
                  <button
                    key={key}
                    className={
                      quickKind === key && composerOpen ? "active" : ""
                    }
                    onClick={() => chooseKind(key)}
                  >
                    <Icon />
                    <span>
                      <strong>{label}</strong>
                      <small>{detail}</small>
                    </span>
                  </button>
                ))}
              </div>
              {composerOpen && (
                <form className="simple-record-form" onSubmit={submitRecord}>
                  <div className="record-core-fields">
                    <label>
                      {ko ? "언제였나요?" : "When?"}
                      <input
                        type="datetime-local"
                        value={occurredAt}
                        onChange={(event) => setOccurredAt(event.target.value)}
                        required
                      />
                    </label>
                    <label>
                      {ko ? "기록 제목" : "Record title"}
                      <input
                        value={recordTitle}
                        onChange={(event) => setRecordTitle(event.target.value)}
                        maxLength={80}
                        required
                      />
                    </label>
                  </div>
                  <label className="record-one-line">
                    <span>
                      {ko ? "한 줄로 적어주세요." : "Add one short note."}
                      <small>
                        {ko
                          ? "예: 저녁부터 묽은 변을 세 번 봤어요."
                          : "e.g. Three loose stools since evening."}
                      </small>
                    </span>
                    <textarea
                      value={recordNote}
                      onChange={(event) => setRecordNote(event.target.value)}
                      rows={3}
                      placeholder={
                        ko ? "평소와 달랐던 점" : "What was different"
                      }
                    />
                  </label>
                  <label className="inline-attachment">
                    <input
                      type="file"
                      multiple
                      accept="image/*,video/mp4,video/quicktime,.pdf,.doc,.docx"
                      onChange={(event) =>
                        setFiles(Array.from(event.target.files || []))
                      }
                    />
                    <span>
                      <Paperclip />
                      <strong>
                        {files.length
                          ? ko
                            ? `${files.length}개 선택됨`
                            : `${files.length} selected`
                          : ko
                            ? "사진, 영상 또는 이전 병원 문서 붙이기"
                            : "Attach photo, video or prior record"}
                      </strong>
                      <small>
                        {ko
                          ? "사진 15MB, 영상과 문서 40MB 이하"
                          : "Images 15MB; video/documents 40MB max"}
                      </small>
                    </span>
                  </label>
                  {files.length > 0 && (
                    <div className="selected-file-chips">
                      {files.map((file) => (
                        <span key={`${file.name}-${file.size}`}>
                          {file.type.startsWith("image/") ? (
                            <ImagePlus />
                          ) : (
                            <FileText />
                          )}
                          {file.name}
                          <button
                            type="button"
                            onClick={() =>
                              setFiles((current) =>
                                current.filter((item) => item !== file),
                              )
                            }
                          >
                            <X />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="priority-row">
                    <span>
                      {ko
                        ? "보호자가 느끼는 걱정 정도"
                        : "Guardian concern level"}
                    </span>
                    {(
                      [
                        ["info", ko ? "참고로 남김" : "For reference"],
                        ["stable", ko ? "호전 또는 안정" : "Stable"],
                        ["attention", ko ? "걱정되는 변화" : "Concerning"],
                        ["critical", ko ? "특히 걱정됨" : "Highly concerning"],
                      ] as const
                    ).map(([value, label]) => (
                      <button
                        type="button"
                        key={value}
                        className={severity === value ? "active" : ""}
                        onClick={() => setSeverity(value)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="detail-toggle"
                    onClick={() => setShowDetailBlocks((value) => !value)}
                  >
                    <PenLine />
                    {showDetailBlocks
                      ? ko
                        ? "상세 기록 접기"
                        : "Hide detailed blocks"
                      : ko
                        ? "필요하면 상세 기록 블록 추가"
                        : "Add detailed blocks if needed"}
                    <ChevronRight />
                  </button>
                  {showDetailBlocks && (
                    <div className="optional-block-editor">
                      <p>
                        {ko
                          ? "여기부터는 선택사항입니다. /를 입력하면 다른 기록 블록을 추가할 수 있습니다."
                          : "Everything below is optional. Type / to add another block."}
                      </p>
                      <BlockEditor
                        blocks={detailBlocks}
                        onChange={setDetailBlocks}
                        surface="member"
                      />
                    </div>
                  )}
                  {uploadProgress > 0 && (
                    <div className="upload-progress">
                      <i style={{ width: `${uploadProgress}%` }} />
                      <span>{uploadProgress}%</span>
                    </div>
                  )}
                  <div className="simple-record-actions">
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={resetComposer}
                    >
                      {ko ? "취소" : "Cancel"}
                    </button>
                    <button className="primary-button" disabled={saving}>
                      <Check />
                      {saving
                        ? ko
                          ? "저장 중…"
                          : "Saving…"
                        : ko
                          ? "이 기록 남기기"
                          : "Save this record"}
                    </button>
                  </div>
                </form>
              )}
            </section>

            <section className="record-and-share-grid">
              <div className="unified-timeline">
                <header>
                  <div>
                    <span>
                      {ko ? "한곳에 모인 기록" : "One continuous record"}
                    </span>
                    <h2>
                      {ko
                        ? `${selectedPet?.name || ""}의 날짜순 기록`
                        : `${selectedPet?.name || ""}’s timeline`}
                    </h2>
                    <p>
                      {ko
                        ? "사진, 문서와 건강기록을 날짜순으로 모아봅니다."
                        : "Photos, files and health notes stay together by date."}
                    </p>
                  </div>
                  <div className="sort-toggle">
                    <button
                      className={sort === "asc" ? "active" : ""}
                      onClick={() => setSort("asc")}
                    >
                      <ArrowDownAZ />
                      {ko ? "과거순" : "Oldest"}
                    </button>
                    <button
                      className={sort === "desc" ? "active" : ""}
                      onClick={() => setSort("desc")}
                    >
                      <ArrowUpAZ />
                      {ko ? "최신순" : "Newest"}
                    </button>
                  </div>
                </header>
                {caseLoading ? (
                  <div className="timeline-loading">
                    <span className="loading-ring" />
                    {ko ? "기록을 불러오는 중" : "Loading"}
                  </div>
                ) : timeline.length ? (
                  <ol>
                    {timeline.map((item) => (
                      <MemberTimelineCard
                        key={item.id}
                        item={item}
                        files={petFiles}
                        ko={ko}
                        onEdit={() => openRecordEdit(item)}
                        onDelete={() => void removeRecord(item)}
                      />
                    ))}
                  </ol>
                ) : (
                  <div className="timeline-empty">
                    <PenLine />
                    <strong>
                      {ko ? "아직 남긴 기록이 없습니다." : "No records yet."}
                    </strong>
                    <p>
                      {ko
                        ? "사진부터 버튼을 눌러 첫 장면을 남겨보세요."
                        : "Start with one photo."}
                    </p>
                    <button
                      className="primary-button"
                      onClick={() => chooseKind("photo")}
                    >
                      <ImagePlus />
                      {ko ? "첫 기록 남기기" : "Add first record"}
                    </button>
                  </div>
                )}
                {petFiles.length > 0 && (
                  <details className="integrated-files">
                    <summary>
                      <Paperclip />
                      {ko
                        ? `첨부파일 ${petFiles.length}개`
                        : `${petFiles.length} attachments`}
                      <ChevronRight />
                    </summary>
                    <div>
                      {petFiles.map((file) => (
                        <article className="integrated-file-row" key={file.id}>
                          <a href={file.href} target="_blank" rel="noreferrer">
                            {file.kind === "media" ? <ImagePlus /> : <FileText />}
                            <span>
                              <strong>{file.title}</strong>
                              <small>
                                {file.createdAt
                                  ? new Date(file.createdAt).toLocaleString(
                                      ko ? "ko-KR" : "en-US",
                                    )
                                  : ""}
                              </small>
                            </span>
                          </a>
                          <button
                            type="button"
                            className="file-manage-button"
                            onClick={() => openFileEdit(file)}
                            aria-label={ko ? `${file.title} 관리` : `Manage ${file.title}`}
                          >
                            <Pencil />
                            {ko ? "관리" : "Manage"}
                          </button>
                        </article>
                      ))}
                    </div>
                  </details>
                )}
                <section className="guardian-question-panel">
                  <header>
                    <div>
                      <HelpCircle />
                      <span>
                        <strong>
                          {ko
                            ? "보호자가 궁금한 점"
                            : "Questions from the guardian"}
                        </strong>
                        <small>
                          {ko
                            ? "진료 전에 궁금한 내용을 메모해 두세요. 의학적 판단이나 요청을 적는 곳은 아닙니다."
                            : "Note what you are wondering before the visit."}
                        </small>
                      </span>
                    </div>
                    <button
                      className="secondary-button"
                      onClick={() => openQuestion()}
                    >
                      <Plus />
                      {ko ? "궁금한 점 적기" : "Add question"}
                    </button>
                  </header>
                  {guardianQuestions.length ? (
                    <ul>
                      {guardianQuestions.map((question) => (
                        <li key={question.id}>
                          <div>
                            <strong>{question.title[ko ? "ko" : "en"]}</strong>
                            {question.detail[ko ? "ko" : "en"] && (
                              <p>{question.detail[ko ? "ko" : "en"]}</p>
                            )}
                          </div>
                          <span>
                            <button
                              className="icon-button"
                              onClick={() => openQuestion(question)}
                              aria-label={
                                ko ? "궁금한 점 수정" : "Edit question"
                              }
                            >
                              <Pencil />
                            </button>
                            <button
                              className="icon-button danger"
                              onClick={() => void removeQuestion(question)}
                              aria-label={
                                ko ? "궁금한 점 삭제" : "Delete question"
                              }
                            >
                              <Trash2 />
                            </button>
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="question-empty">
                      {ko
                        ? "아직 적어 둔 내용이 없습니다."
                        : "No questions added yet."}
                    </p>
                  )}
                </section>
              </div>

              <aside className="share-workbench">
                <span className="share-icon">
                  <Link2 />
                </span>
                <h2>{managedCopy.actionTitle}</h2>
                <p>{managedCopy.actionBody}</p>
                <dl>
                  <div>
                    <dt>{ko ? "기록 범위" : "Record"}</dt>
                    <dd>{selectedPet?.name}</dd>
                  </div>
                  <div>
                    <dt>{ko ? "현재 상태" : "Status"}</dt>
                    <dd>
                      {activeShare
                        ? ko
                          ? "공유 중"
                          : "Active"
                        : ko
                          ? "비공개"
                          : "Private"}
                    </dd>
                  </div>
                  {activeShare && (
                    <div>
                      <dt>{ko ? "열람" : "Views"}</dt>
                      <dd>
                        {activeShare.viewCount}
                        {ko ? "회" : ""}
                      </dd>
                    </div>
                  )}
                </dl>
                <button
                  className="primary-button"
                  onClick={() => setModal("share")}
                >
                  <Link2 />
                  {activeShare
                    ? ko
                      ? "공유 링크 관리"
                      : "Manage link"
                    : managedCopy.primaryLabel}
                </button>
              </aside>
            </section>
          </>
        )}
      </main>

      <MemberBottomBar
        hasPet={Boolean(selectedPet)}
        onRecord={() => (selectedPet ? chooseKind("photo") : setModal("pet"))}
        onShare={() => (selectedPet ? setModal("share") : setModal("pet"))}
        ko={ko}
      />

      {modal === "pet" && (
        <Modal
          title={ko ? "반려동물 등록" : "Add a pet"}
          description={
            ko
              ? "이름만으로 시작하고 나머지는 나중에 채워도 됩니다."
              : "Start with a name and add details later."
          }
          onClose={() => setModal(null)}
        >
          <form className="member-form" onSubmit={submitPet}>
            <label>
              {ko ? "이름" : "Name"}
              <input name="name" autoFocus required />
            </label>
            <div className="form-grid">
              <label>
                {ko ? "동물 종류" : "Species"}
                <select name="species">
                  <option value="cat">{ko ? "고양이" : "Cat"}</option>
                  <option value="dog">{ko ? "강아지" : "Dog"}</option>
                </select>
              </label>
              <label>
                {ko ? "성별" : "Sex"}
                <select name="sex">
                  <option value="unknown">{ko ? "확인 전" : "Unknown"}</option>
                  <option value="male">{ko ? "수컷" : "Male"}</option>
                  <option value="female">{ko ? "암컷" : "Female"}</option>
                </select>
              </label>
            </div>
            <label>
              {ko ? "생년월일 또는 추정일" : "Birth date or estimate"}
              <input type="date" name="estimatedBirth" />
            </label>
            <div className="modal-actions">
              <button
                type="button"
                className="secondary-button"
                onClick={() => setModal(null)}
              >
                {ko ? "취소" : "Cancel"}
              </button>
              <button className="primary-button">
                {ko ? "등록하기" : "Add pet"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {modal === "pet-edit" && selectedPet && selectedPetRecord && (
        <Modal
          wide
          title={
            ko ? `${selectedPet.name} 정보 수정` : `Edit ${selectedPet.name}`
          }
          description={
            ko
              ? "여기서 수정한 내용은 현재 보안 공유 화면에도 반영됩니다."
              : "Changes also appear on the active secure share page."
          }
          onClose={() => setModal(null)}
        >
          <form
            className="member-form pet-profile-form"
            onSubmit={submitPetEdit}
          >
            <div className="form-grid">
              <label>
                {ko ? "이름" : "Name"}
                <input name="name" defaultValue={selectedPet.name} required />
              </label>
              <label>
                {ko ? "동물 종류" : "Species"}
                <select name="species" defaultValue={selectedPet.species}>
                  <option value="cat">{ko ? "고양이" : "Cat"}</option>
                  <option value="dog">{ko ? "강아지" : "Dog"}</option>
                </select>
              </label>
              <label>
                {ko ? "성별" : "Sex"}
                <select name="sex" defaultValue={selectedPet.sex || "unknown"}>
                  <option value="unknown">{ko ? "확인 전" : "Unknown"}</option>
                  <option value="male">{ko ? "수컷" : "Male"}</option>
                  <option value="female">{ko ? "암컷" : "Female"}</option>
                </select>
              </label>
              <label>
                {ko ? "생년월일 또는 추정일" : "Birth date or estimate"}
                <input
                  type="date"
                  name="estimatedBirth"
                  defaultValue={selectedPetRecord.estimatedBirth || ""}
                />
              </label>
              <label>
                {ko ? "입양일" : "Adoption date"}
                <input
                  type="date"
                  name="adoptionDate"
                  defaultValue={selectedPetRecord.adoptionDate || ""}
                />
              </label>
              <label>
                {ko ? "병원에 가기로 한 날" : "Planned clinic visit"}
                <input
                  type="date"
                  name="clinicVisitDate"
                  defaultValue={caseData?.clinicVisitDate || ""}
                />
              </label>
              <label>
                {ko ? "입양처" : "Adoption source"}
                <input
                  name="adoptionSource"
                  defaultValue={selectedPetRecord.adoptionSource.ko || ""}
                />
              </label>
              <label>
                {ko ? "동물등록번호" : "Registration number"}
                <input
                  name="registration"
                  defaultValue={selectedPetRecord.registration || ""}
                />
              </label>
              <label>
                {ko ? "보험" : "Insurance"}
                <input
                  name="insurance"
                  defaultValue={selectedPetRecord.insurance?.ko || ""}
                />
              </label>
            </div>
            <label>
              {ko ? "처음 만났을 때 상태" : "Initial condition"}
              <textarea
                name="firstCondition"
                rows={3}
                defaultValue={selectedPetRecord.firstCondition.ko || ""}
              />
            </label>
            <label>
              {ko
                ? "입양 당시 전달받은 설명"
                : "Information received at adoption"}
              <textarea
                name="intakeStatement"
                rows={3}
                defaultValue={selectedPetRecord.intakeStatement.ko || ""}
              />
            </label>
            <div className="form-grid">
              <label>
                {ko ? "평소 식욕" : "Usual appetite"}
                <input
                  name="appetite"
                  defaultValue={selectedPetRecord.appetite.ko || ""}
                />
              </label>
              <label>
                {ko ? "평소 먹는 것" : "Usual feeding"}
                <input
                  name="feeding"
                  defaultValue={selectedPetRecord.feeding.ko || ""}
                />
              </label>
              <label>
                {ko ? "평소 배변과 소변" : "Usual toilet habits"}
                <input
                  name="stool"
                  defaultValue={selectedPetRecord.stool.ko || ""}
                />
              </label>
              <label>
                {ko ? "성격과 생활 습관" : "Personality and habits"}
                <input
                  name="personality"
                  defaultValue={selectedPetRecord.personality.ko || ""}
                />
              </label>
            </div>
            <div className="modal-actions sticky">
              <button
                type="button"
                className="secondary-button"
                onClick={() => setModal(null)}
              >
                {ko ? "취소" : "Cancel"}
              </button>
              <button className="primary-button" disabled={saving}>
                <Check />
                {ko ? "수정내용 저장" : "Save changes"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {modal === "record-edit" && editingRecord && (
        <Modal
          wide
          title={ko ? "기록 수정" : "Edit record"}
          description={
            ko
              ? "보호자가 관찰한 사실을 고칩니다. 저장하면 현재 공유 화면에도 반영됩니다."
              : "Edit the guardian observation. Changes appear on the active share page."
          }
          onClose={() => {
            setEditingRecord(null);
            setEditingBlocks([]);
            setModal(null);
          }}
        >
          <form
            className="member-form record-edit-form"
            onSubmit={submitRecordEdit}
          >
            <div className="form-grid">
              <label>
                {ko ? "언제 있었나요?" : "When?"}
                <input
                  name="occurredAt"
                  type="datetime-local"
                  defaultValue={`${editingRecord.date}T${editingRecord.time || "00:00"}`}
                  required
                />
              </label>
              <label>
                {ko ? "기록 종류" : "Type"}
                <select name="category" defaultValue={editingRecord.category}>
                  <option value="observation">
                    {ko ? "일상 관찰" : "Observation"}
                  </option>
                  <option value="symptom">{ko ? "증상" : "Symptom"}</option>
                  <option value="medication">
                    {ko ? "투약과 처치" : "Medication"}
                  </option>
                  <option value="visit">
                    {ko ? "병원 방문" : "Clinic visit"}
                  </option>
                  <option value="measurement">
                    {ko ? "체중과 체온" : "Measurement"}
                  </option>
                </select>
              </label>
            </div>
            <label>
              {ko ? "제목" : "Title"}
              <input
                name="title"
                defaultValue={editingRecord.title.ko}
                required
              />
            </label>
            <label>
              {ko ? "관찰한 내용" : "What was observed"}
              <textarea
                name="detail"
                rows={4}
                defaultValue={editingRecord.description.ko}
                required
              />
            </label>
            <label>
              {ko ? "보호자가 느끼는 걱정 정도" : "Guardian concern level"}
              <select name="severity" defaultValue={editingRecord.severity}>
                <option value="info">
                  {ko ? "참고로 남김" : "For reference"}
                </option>
                <option value="stable">
                  {ko ? "호전되거나 안정됨" : "Improving or stable"}
                </option>
                <option value="attention">
                  {ko ? "걱정되는 변화" : "Concerning change"}
                </option>
                <option value="critical">
                  {ko ? "특히 걱정되는 변화" : "Highly concerning change"}
                </option>
              </select>
            </label>
            <div className="record-edit-blocks">
              <strong>
                {ko ? "상세 기록 블록" : "Detailed record blocks"}
              </strong>
              <p>
                {ko
                  ? "필요한 내용만 남기거나 순서를 바꿀 수 있습니다."
                  : "Keep only what is useful or change the order."}
              </p>
              <BlockEditor
                blocks={editingBlocks}
                onChange={setEditingBlocks}
                surface="member"
              />
            </div>
            <div className="modal-actions sticky">
              <button
                type="button"
                className="secondary-button"
                onClick={() => {
                  setEditingRecord(null);
                  setModal(null);
                }}
              >
                {ko ? "취소" : "Cancel"}
              </button>
              <button className="primary-button" disabled={saving}>
                <Check />
                {ko ? "수정내용 저장" : "Save changes"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {modal === "question" && selectedPet && (
        <Modal
          title={
            ko
              ? `${selectedPet.name}에 대해 궁금한 점`
              : `Question about ${selectedPet.name}`
          }
          description={
            ko
              ? "보호자가 걱정되거나 궁금한 점을 적는 곳입니다. 진단이나 처치를 요청하는 문장으로 작성할 필요는 없습니다."
              : "Write what you are worried or curious about. No clinical instruction is needed."
          }
          onClose={() => {
            setEditingQuestion(null);
            setModal(null);
          }}
        >
          <form className="member-form" onSubmit={submitQuestion}>
            <label>
              {ko ? "궁금한 점" : "Question"}
              <input
                name="title"
                defaultValue={editingQuestion?.title.ko || ""}
                placeholder={
                  ko
                    ? "예: 설사가 계속되면 언제 다시 내원해야 할까요?"
                    : "e.g. When should we return if diarrhea continues?"
                }
                required
              />
            </label>
            <label>
              {ko ? "함께 전할 내용 (선택)" : "Additional context (optional)"}
              <textarea
                name="detail"
                rows={4}
                defaultValue={editingQuestion?.detail.ko || ""}
                placeholder={
                  ko
                    ? "걱정되는 이유나 집에서 관찰한 내용을 짧게 적어주세요."
                    : "Add a short reason or observation."
                }
              />
            </label>
            <div className="modal-actions">
              <button
                type="button"
                className="secondary-button"
                onClick={() => {
                  setEditingQuestion(null);
                  setModal(null);
                }}
              >
                {ko ? "취소" : "Cancel"}
              </button>
              <button className="primary-button" disabled={saving}>
                <Check />
                {ko ? "저장" : "Save"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {modal === "profile-photo" && (
        <Modal
          title={
            ko ? `${selectedPet?.name || ""} 대표 사진` : "Pet profile photo"
          }
          description={
            ko
              ? "병원 전달 화면에서 가장 먼저 보이는 사진입니다."
              : "This is the first photo the clinic sees."
          }
          onClose={() => setModal(null)}
        >
          <form className="member-form" onSubmit={submitProfilePhoto}>
            <label className="file-drop">
              <UploadCloud />
              <strong>{ko ? "사진 선택" : "Choose photo"}</strong>
              <span>JPG, PNG, WEBP, HEIC / 최대 15MB</span>
              <input type="file" name="file" accept="image/*" required />
            </label>
            {uploadProgress > 0 && (
              <div className="upload-progress">
                <i style={{ width: `${uploadProgress}%` }} />
                <span>{uploadProgress}%</span>
              </div>
            )}
            <div className="modal-actions">
              <button
                type="button"
                className="secondary-button"
                onClick={() => setModal(null)}
              >
                {ko ? "취소" : "Cancel"}
              </button>
              <button className="primary-button">
                {ko ? "사진 저장" : "Save photo"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {modal === "file" && editingFile && (
        <Modal
          title={ko ? "첨부파일 관리" : "Manage attachment"}
          description={
            ko
              ? "이름을 바꾸면 현재 보안 공유 화면에도 반영됩니다. 삭제한 파일은 Google Drive 휴지통으로 이동합니다."
              : "Renaming updates the secure share page. Deleted files move to the Google Drive trash."
          }
          onClose={() => {
            setEditingFile(null);
            setModal(null);
          }}
        >
          <form className="member-form" onSubmit={submitFileEdit}>
            <label>
              {ko ? "보안 공유 화면에 보일 이름" : "Name shown on the share page"}
              <input name="title" defaultValue={editingFile.title} required />
            </label>
            <a
              className="file-preview-link"
              href={editingFile.href}
              target="_blank"
              rel="noreferrer"
            >
              {editingFile.kind === "media" ? <ImagePlus /> : <FileText />}
              {ko ? "원본 파일 열기" : "Open original file"}
            </a>
            <div className="modal-actions modal-actions-between">
              <button
                type="button"
                className="secondary-button danger-text"
                disabled={saving}
                onClick={() => void removeFile()}
              >
                <Trash2 />
                {ko ? "파일 삭제" : "Delete file"}
              </button>
              <button className="primary-button" disabled={saving}>
                <Check />
                {ko ? "이름 저장" : "Save name"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {modal === "share" && (
        <Modal
          title={ko ? "병원에 보낼 보안 링크" : "Secure clinic link"}
          description={
            ko
              ? `${selectedPet?.name || "반려동물"}의 사진, 기록, 첨부자료를 읽기 전용으로 전달합니다.`
              : "Share photos, records and attachments as read only."
          }
          onClose={() => {
            setModal(null);
            setLastShareUrl("");
          }}
        >
          <form className="member-form" onSubmit={submitShare}>
            <label>
              {ko ? "링크 만료" : "Expiry"}
              <select name="expiresInDays" defaultValue="7">
                <option value="1">{ko ? "1일 후" : "1 day"}</option>
                <option value="3">{ko ? "3일 후" : "3 days"}</option>
                <option value="7">{ko ? "7일 후" : "7 days"}</option>
                <option value="30">{ko ? "30일 후" : "30 days"}</option>
              </select>
            </label>
            <p className="share-warning">
              <FileLock2 />
              {ko
                ? "링크를 받은 사람은 만료 전까지 볼 수 있습니다. 진료가 끝나면 철회해 주세요."
                : "Anyone with the link can view it until expiry. Revoke it after the visit."}
            </p>
            {lastShareUrl && (
              <label>
                {ko ? "생성된 링크" : "Generated link"}
                <input
                  value={lastShareUrl}
                  readOnly
                  onFocus={(event) => event.currentTarget.select()}
                />
              </label>
            )}
            <div className="modal-actions">
              {activeShare && (
                <button
                  type="button"
                  className="secondary-button danger-text"
                  onClick={() => void removeActiveShare()}
                >
                  {ko ? "기존 링크 철회" : "Revoke current link"}
                </button>
              )}
              <button className="primary-button">
                <Link2 />
                {ko ? "새 링크 만들고 복사" : "Create and copy"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {modal === "profile" && (
        <Modal
          wide
          title={ko ? "내 계정" : "My account"}
          description={
            ko
              ? "회원정보와 로그인 정보를 직접 관리합니다."
              : "Manage your profile and sign-in details."
          }
          onClose={() => setModal(null)}
        >
          <ProfilePanel
            preview={preview}
            ko={ko}
            user={dashboard.user}
            csrf={csrf}
            onNotice={showNotice}
            onRefresh={async () => {
              await refresh();
              await refreshDashboard();
            }}
            onLogout={() => void logout()}
          />
        </Modal>
      )}
    </div>
  );
}

function MemberTimelineCard({
  item,
  files,
  ko,
  onEdit,
  onDelete,
}: {
  item: TimelineItem;
  files: MemberFileSummary[];
  ko: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const mediaBlocks =
    item.blocks?.filter(
      (block) => block.assetKind === "image" || block.assetKind === "video",
    ) || [];
  const textBlocks =
    item.blocks
      ?.filter(
        (block) =>
          block.content && !block.type.startsWith("heading") && !block.assetId,
      )
      .slice(0, 3) || [];
  return (
    <li className={`member-timeline-card severity-${item.severity}`}>
      <time>
        <strong>
          {new Date(`${item.date}T00:00:00`).toLocaleDateString(
            ko ? "ko-KR" : "en-US",
            { month: "short", day: "numeric" },
          )}
        </strong>
        <small>{item.time || ""}</small>
      </time>
      <article>
        <div className="timeline-card-actions">
          <button
            type="button"
            onClick={onEdit}
            aria-label={ko ? "기록 수정" : "Edit record"}
          >
            <Pencil />
          </button>
          <button
            type="button"
            onClick={onDelete}
            aria-label={ko ? "기록 삭제" : "Delete record"}
          >
            <Trash2 />
          </button>
        </div>
        {mediaBlocks.length > 0 && (
          <div className="member-timeline-media-grid">
            {mediaBlocks.map((block) => {
              const file = files.find((item) => item.id === block.assetId);
              return file ? (
                <figure key={block.id}>
                  {block.assetKind === "video" ? (
                    <video
                      src={file.href}
                      controls
                      preload="metadata"
                      playsInline
                    />
                  ) : (
                    <img src={file.href} alt={block.assetName || file.title} />
                  )}
                  <figcaption>{block.assetName || file.title}</figcaption>
                </figure>
              ) : (
                <div key={block.id} className="timeline-media-note">
                  <ImagePlus />
                  <span>{ko ? "첨부한 사진 또는 영상" : "Media attachment"}</span>
                </div>
              );
            })}
          </div>
        )}
        <div className="timeline-card-heading">
          <span>
            {item.category === "visit"
              ? ko
                ? "병원 방문"
                : "Clinic visit"
              : item.category === "medication"
                ? ko
                  ? "투약"
                  : "Medication"
                : item.category === "symptom"
                  ? ko
                    ? "증상"
                    : "Symptom"
                  : ko
                    ? "관찰"
                    : "Observation"}
          </span>
          <h3>{item.title[ko ? "ko" : "en"]}</h3>
        </div>
        {textBlocks.length ? (
          textBlocks.map((block) => <p key={block.id}>{block.content}</p>)
        ) : (
          <p>{item.description[ko ? "ko" : "en"]}</p>
        )}
        <small className="record-source">
          <ShieldCheck />
          {ko ? "보호자 기록" : "Guardian record"}
        </small>
      </article>
    </li>
  );
}

function MemberBottomBar({
  hasPet,
  onRecord,
  onShare,
  ko,
}: {
  hasPet: boolean;
  onRecord: () => void;
  onShare: () => void;
  ko: boolean;
}) {
  const visible = useScrollDirection();
  return (
    <nav
      className={`member-action-bar glass-panel ${visible ? "is-visible" : "is-hidden"}`}
      aria-label={ko ? "빠른 작업" : "Quick actions"}
    >
      <a href="#new-record">
        <HeartPulse />
        <span>{ko ? "기록" : "Record"}</span>
      </a>
      <button className="main-action" onClick={onRecord}>
        <Plus />
        <span>
          {hasPet ? (ko ? "남기기" : "Add") : ko ? "등록" : "Add pet"}
        </span>
      </button>
      <button onClick={onShare}>
        <Link2 />
        <span>{ko ? "병원에 보내기" : "Share"}</span>
      </button>
    </nav>
  );
}

function ProfilePanel({
  preview,
  ko,
  user,
  csrf,
  onNotice,
  onRefresh,
  onLogout,
}: {
  preview: boolean;
  ko: boolean;
  user: MemberDashboardData["user"];
  csrf: string;
  onNotice: (value: string) => void;
  onRefresh: () => Promise<void>;
  onLogout: () => void;
}) {
  const submitProfile = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (preview) {
      onNotice(
        ko
          ? "미리보기에서 기본 정보 저장을 확인했습니다."
          : "Preview profile save checked.",
      );
      return;
    }
    const data = new FormData(event.currentTarget);
    await updateMemberProfile(
      {
        name: String(data.get("name")),
        phone: String(data.get("phone")),
        address: String(data.get("address")),
      },
      csrf,
    );
    await onRefresh();
    onNotice(ko ? "회원정보를 저장했습니다." : "Profile saved.");
  };
  const submitEmail = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (preview) {
      onNotice(
        ko
          ? "미리보기에서 이메일 인증 흐름을 확인했습니다."
          : "Preview email flow checked.",
      );
      return;
    }
    const data = new FormData(event.currentTarget);
    await requestEmailChange(String(data.get("email")), csrf);
    onNotice(
      ko ? "새 이메일로 인증 링크를 보냈습니다." : "Verification email sent.",
    );
  };
  const submitPassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (preview) {
      event.currentTarget.reset();
      onNotice(
        ko
          ? "미리보기에서 비밀번호 변경 흐름을 확인했습니다."
          : "Preview password flow checked.",
      );
      return;
    }
    const data = new FormData(event.currentTarget);
    await changePassword(
      String(data.get("currentPassword")),
      String(data.get("nextPassword")),
      csrf,
    );
    event.currentTarget.reset();
    onNotice(ko ? "비밀번호를 변경했습니다." : "Password changed.");
  };
  return (
    <div className="profile-grid profile-modal-grid">
      <form
        className="profile-card"
        onSubmit={(event) => void submitProfile(event)}
      >
        <div>
          <CircleUserRound />
          <span>
            <h2>{ko ? "기본 정보" : "Basic information"}</h2>
            <p>{user.loginId}</p>
          </span>
        </div>
        <label>
          {ko ? "이름" : "Name"}
          <input name="name" defaultValue={user.name} required />
        </label>
        <label>
          {ko ? "연락처" : "Phone"}
          <input
            name="phone"
            autoComplete="tel"
            defaultValue={user.phone || ""}
          />
        </label>
        <label>
          {ko ? "주소" : "Address"}
          <input
            name="address"
            autoComplete="street-address"
            defaultValue={user.address || ""}
          />
        </label>
        <button className="primary-button">
          {ko ? "기본 정보 저장" : "Save"}
        </button>
      </form>
      <form
        className="profile-card"
        onSubmit={(event) => void submitEmail(event)}
      >
        <div>
          <ShieldCheck />
          <span>
            <h2>{ko ? "이메일" : "Email"}</h2>
            <p>
              {user.emailVerified
                ? ko
                  ? "인증 완료"
                  : "Verified"
                : ko
                  ? "인증 필요"
                  : "Verification required"}
            </p>
          </span>
        </div>
        <label>
          {ko ? "현재 이메일" : "Current email"}
          <input value={user.email} readOnly />
        </label>
        <label>
          {ko ? "새 이메일" : "New email"}
          <input name="email" type="email" required />
        </label>
        <button className="secondary-button">
          {ko ? "인증 링크 보내기" : "Send verification"}
        </button>
      </form>
      <form
        className="profile-card"
        onSubmit={(event) => void submitPassword(event)}
      >
        <div>
          <FileLock2 />
          <span>
            <h2>{ko ? "비밀번호" : "Password"}</h2>
            <p>{ko ? "10자 이상" : "10+ characters"}</p>
          </span>
        </div>
        <label>
          {ko ? "현재 비밀번호" : "Current password"}
          <input name="currentPassword" type="password" required />
        </label>
        <label>
          {ko ? "새 비밀번호" : "New password"}
          <input name="nextPassword" type="password" minLength={10} required />
        </label>
        <button className="secondary-button">
          {ko ? "비밀번호 변경" : "Change password"}
        </button>
      </form>
      <button className="profile-logout" onClick={onLogout}>
        <LogOut />
        {ko ? "로그아웃" : "Sign out"}
      </button>
    </div>
  );
}

function Modal({
  title,
  description,
  onClose,
  children,
  wide = false,
}: {
  title: string;
  description: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className="member-modal-backdrop" onMouseDown={onClose}>
      <section
        className={`member-modal ${wide ? "wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="member-modal-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <h2 id="member-modal-title">{title}</h2>
            <p>{description}</p>
          </div>
          <button onClick={onClose} aria-label="닫기">
            <X />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

function EmptyPets({ ko, onAdd }: { ko: boolean; onAdd: () => void }) {
  return (
    <section className="member-first-step">
      <div className="first-step-visual">
        <Cat />
      </div>
      <span>{ko ? "먼저 한 가지만" : "One thing first"}</span>
      <h2>
        {ko
          ? "기록할 반려동물을 알려주세요."
          : "Who are we keeping records for?"}
      </h2>
      <p>
        {ko
          ? "이름만 등록하면 바로 사진과 짧은 메모를 남길 수 있습니다. 자세한 정보는 나중에 채워도 됩니다."
          : "Add a name to start with photos and short notes. Details can wait."}
      </p>
      <button className="primary-button" onClick={onAdd}>
        <Plus />
        {ko ? "반려동물 등록하기" : "Add a pet"}
      </button>
      <div>
        <span>
          <ImagePlus />
          {ko ? "사진부터 시작" : "Start with a photo"}
        </span>
        <span>
          <Link2 />
          {ko ? "병원에 링크 전달" : "Share one link"}
        </span>
        <span>
          <ShieldCheck />
          {ko ? "기본 비공개" : "Private by default"}
        </span>
      </div>
    </section>
  );
}

function ConnectionState({
  onRetry,
  ko,
}: {
  onRetry: () => void;
  ko: boolean;
}) {
  return (
    <section className="connection-state">
      <RefreshCw />
      <h1>
        {ko ? "기록을 불러오지 못했습니다." : "Could not load your records."}
      </h1>
      <p>{ko ? "잠시 후 다시 시도해 주세요." : "Please try again shortly."}</p>
      <button className="secondary-button" onClick={onRetry}>
        <RefreshCw />
        {ko ? "다시 시도" : "Retry"}
      </button>
    </section>
  );
}
